import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { createAssistant, getAssistants, deleteAssistant, toggleAssistantStatus } from '../services/assistantService';

export default function Assistant() {
  const navigate = useNavigate();
  const [assistants, setAssistants] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [assistantName, setAssistantName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load assistants on mount
  useEffect(() => {
    loadAssistants();
  }, []);

  const loadAssistants = async () => {
    try {
      const data = await getAssistants();
      setAssistants(data);
    } catch (error) {
      console.error('Error loading assistants:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load assistants',
        severity: 'error',
      });
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setError('');
    setAssistantName('');
    setSelectedFile(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
    setAssistantName('');
    setSelectedFile(null);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    setError('');

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    // Validate file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size must not exceed 5 MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleDownloadTemplate = () => {
    // Create a simple CSV template
    const csvContent = 'Number\n1\n2\n3\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'assistant-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!assistantName.trim()) {
      setError('Please enter an assistant name');
      return;
    }

    if (!selectedFile) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);

    try {
      // Create assistant
      await createAssistant(assistantName, selectedFile);

      // Success - close dialog and refresh list
      handleCloseDialog();
      loadAssistants();

      // Show success message
      setSnackbar({
        open: true,
        message: `Assistant "${assistantName}" created successfully!`,
        severity: 'success',
      });
    } catch (err) {
      setError('Failed to create assistant. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (assistant) => {
    if (window.confirm(`Are you sure you want to delete "${assistant.name}"?`)) {
      try {
        await deleteAssistant(assistant.assistantId || assistant.id);
        loadAssistants();
        setSnackbar({
          open: true,
          message: `Assistant "${assistant.name}" deleted successfully!`,
          severity: 'success',
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to delete assistant',
          severity: 'error',
        });
      }
    }
  };

  const handleToggleStatus = async (assistant) => {
    try {
      await toggleAssistantStatus(assistant.assistantId || assistant.id);
      loadAssistants();
      setSnackbar({
        open: true,
        message: `Assistant status updated to ${assistant.status === 'active' ? 'inactive' : 'active'}`,
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update status',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Assistant
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Create Assistant
        </Button>
      </Box>

      {/* Assistants List */}
      {assistants.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No assistants yet. Click "Create Assistant" to get started.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>File</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assistants.map((assistant) => (
                <TableRow
                  key={assistant.assistantId || assistant.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/assistant/${assistant.assistantId || assistant.id}`)}
                >
                  <TableCell>{assistant.name}</TableCell>
                  <TableCell>{assistant.originalFileName || assistant.fileName}</TableCell>
                  <TableCell>{formatFileSize(assistant.fileSizeBytes || assistant.fileSize)}</TableCell>
                  <TableCell>{formatDate(assistant.createdAt)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={assistant.status}
                        color={assistant.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleStatus(assistant);
                        }}
                        title={`Toggle status (currently ${assistant.status})`}
                      >
                        {assistant.status === 'active' ? <ToggleOnIcon color="success" /> : <ToggleOffIcon />}
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(assistant);
                      }}
                      title="Delete assistant"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Assistant Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Create New Assistant
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Download Template Button */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              fullWidth
            >
              Download CSV Template
            </Button>
          </Box>

          {/* Assistant Name Field */}
          <TextField
            label="Assistant Name"
            fullWidth
            value={assistantName}
            onChange={(e) => setAssistantName(e.target.value)}
            sx={{ mb: 3 }}
            placeholder="Enter assistant name"
          />

          {/* File Upload Area */}
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: dragActive ? 'primary.main' : 'rgba(255, 255, 255, 0.23)',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
            onClick={() => document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".csv"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 1 }}>
              {selectedFile ? selectedFile.name : 'Drag and drop a file here or click to select a file locally'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Maximum file size: 5 MB
            </Typography>
            {selectedFile && (
              <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
                File selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Typography>
            )}
          </Box>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={uploading}
          >
            {uploading ? 'Creating...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
